import { computed, inject, Injectable, resource } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { map } from 'rxjs';
import { TuyauService } from '@core/tuyau';

@Injectable({ providedIn: 'root' })
export class AccountService {
  private readonly oidc = inject(OidcSecurityService);
  private readonly tuyau = inject(TuyauService);
  private readonly transloco = inject(TranslocoService);

  private readonly isAuthenticated = toSignal(
    this.oidc.isAuthenticated$.pipe(
      map(({ isAuthenticated }) => isAuthenticated)
    ),
    { initialValue: false }
  );

  get originalRoute(): string | null {
    return sessionStorage.getItem('originalRoute');
  }

  set originalRoute(value: string | null) {
    if (value) {
      sessionStorage.setItem('originalRoute', value);
    } else {
      sessionStorage.removeItem('originalRoute');
    }
  }

  accountResource = resource({
    params: () => ({ isAuthenticated: this.isAuthenticated() }),
    loader: async ({ params }) => {
      if (params.isAuthenticated) {
        const response = await this.tuyau.client.api.employeeV2.account.show(
          {}
        );

        return response.data;
      }
      return null;
    },
  });

  /**
   * The account, or null — the only read of the resource, because `value()`
   * raises `ResourceValueError` in the error state instead of answering
   * undefined. Everything below runs inside a computed the layout renders on
   * every screen, so a refused account has to read as "no account" rather than
   * as an exception that takes the navigation rail down with it.
   */
  private readonly account = computed(() =>
    this.accountResource.hasValue() ? this.accountResource.value() : null
  );

  /**
   * Whether the incidents area is this employee's to see: they must do property
   * management *and* be in the beta group. Both come from the account response,
   * and both are enforced again by the backend — this only decides what the
   * portal shows, so a stale or missing account hides the area rather than
   * risking a screen that 403s on every request.
   */
  readonly canAccessIncidents = computed(() => {
    const account = this.account();
    return Boolean(
      account?.accessRights?.isPropertyManagementUser &&
      account?.incidentsBetaAccess
    );
  });

  /**
   * Whether the account has settled, either way. Guards wait for this: until it
   * is true, "no access" only means "not loaded yet".
   *
   * A resolved *null* does not count. The resource is keyed on `isAuthenticated`,
   * a `toSignal` that starts at `false`, so before OIDC has emitted the loader
   * short-circuits to `null` and the resource reports itself resolved with no
   * account — which a guard reading "settled, no rights" would turn into a
   * redirect away from a deep link the employee is perfectly entitled to.
   */
  readonly isAccountSettled = computed(
    () => this.accountResource.status() === 'error' || this.account() != null
  );

  readonly profile = computed(() => {
    const account = this.account();
    if (!account) return null;

    const initials = (account.fullName ?? '')
      .split(' ')
      .filter(Boolean)
      .map((part: string) => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);

    return { ...account, initials };
  });

  async changeLanguage(lang: string) {
    this.transloco.setActiveLang(lang);
    // await this.tuyau.client.api.employeeV2.account.patch({
    //   body: { locale: lang.replace('-CH', '') },
    // });
  }

  logout() {
    this.oidc.logoff().subscribe();
  }
}
